'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { reduxLogger } from '@/core/utils/logger';
import {
  useGetOrCreateOnboardingSessionQuery,
  useSubmitOnboardingStepMutation,
} from '@/api/features/onboarding';
import type { OnboardingSession } from '@/api/features/onboarding';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { WelcomeModal } from './WelcomeModal';
import { TourCompleteModal } from './TourCompleteModal';
import { ADMIN_TOUR_STEPS, REQUIRED_HOTSPOTS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = ADMIN_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * AdminTourOverlay - Renders the AdminTour step UI on top of the dashboard.
 *
 * Only renders when the active tour is 'admin'. Handles onboarding backend
 * submission locally via its own RTK Query mutation.
 */
export function AdminTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isAdminTour = activeTourId === 'admin';

  // Auto-advance polling (navigateTo, advanceOnNavigate, advanceOnElement, advanceOnElementRemoved)
  useTourAutoAdvance(isAdminTour, currentStep, nextStep);

  // Backend submission for onboarding flow
  const { data: session } = useGetOrCreateOnboardingSessionQuery(undefined, {
    refetchOnFocus: false,
    refetchOnMountOrArgChange: false,
  });
  const [submitStepMutation, { isLoading: isSubmitting }] = useSubmitOnboardingStepMutation();

  const currentOnboardingStep = (session as OnboardingSession | undefined)?.currentStep as
    | string
    | undefined;
  const isOnboarding = currentOnboardingStep === 'demo';

  const handleFinish = useCallback(async () => {
    if (isOnboarding) {
      reduxLogger.info('[AdminTour] Completing tour, submitting demo step');
      const hotspots = [...REQUIRED_HOTSPOTS];
      const idempotencyKey = crypto.randomUUID();
      const result = await submitStepMutation({
        step: 'demo' as OnboardingSession['currentStep'],
        data: { completedHotspots: hotspots },
        idempotencyKey,
      }).unwrap();
      reduxLogger.info('[AdminTour] Demo step submitted successfully', { nextRoute: result.nextRoute });
      completeTourWithTracking('admin');
      router.push(result.nextRoute);
    } else {
      reduxLogger.info('[AdminTour] Manual tour completed');
      completeTourWithTracking('admin');
      router.push('/dashboard?view=all');
    }
  }, [isOnboarding, submitStepMutation, completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isAdminTour || !isActive || !currentStep) return null;

  // Calculate backdrop visibility
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'tour_complete' ? (
            <TourCompleteModal onFinish={handleFinish} isSubmitting={isSubmitting} />
          ) : (
            <WelcomeModal onComplete={nextStep} />
          )}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
